<template>
  <div :class="containerClass">
    <div class="vas-dividers">
      <div
          v-for="(position, index) in dividerPositions"
          :key="'divider-' + index"
          class="vas-divider"
          :style="{ left: `${position}%` }"
      ></div>
    </div>

    <div class="vas-pointer" :style="pointerStyle">
      <div class="vas-value">{{ value }}</div>
      <div class="vas-arrow" :style="{ borderTopColor: arrowColor }"></div>
    </div>

    <div class="vas-bar">
      <div class="vas-colored-fill" :style="coloredFillStyle"></div>
      <div class="vas-grey-fill" :style="greyFillStyle"></div>
      <div
          v-for="(segment, index) in segments"
          :key="index"
          class="vas-segment"
          :style="{ width: `${((segment.to - segment.from) / total) * 100}%` }"
      >
        <span class="segment-label">{{ segment.label }}</span>
      </div>
    </div>

    <div class="vas-scale">
      <span
          v-for="(segment, index) in segments"
          :key="'tick-' + index"
          class="tick"
          :style="{ left: `${((segment.from - 50) / total) * 100}%` }"
      >
        {{ segment.from }}
      </span>
      <span class="tick" :style="{ left: '100%' }">150</span>
    </div>

    <div v-if="scaleLabel" class="scale-label">
      {{ scaleLabel }}
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import {toRefs} from "@vue/reactivity";
export default {
  name: "visual-analog-scale",
  props: {
    value: {
      type: Number,
      required: true,
    },
    scaleLabel: {
      type: String,
      default: '',
    },
    highlightedGreen: {
      type: Boolean,
      default: false,
    },
    highlightedRed: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {

    const dataProps = toRefs(props);
    const total = 100;

    const containerClass = computed(() => {
      return {
        'vas-container': true,
        'vas-highlighted': dataProps.highlightedGreen.value || dataProps.highlightedRed.value,
        'vas-highlighted-green': dataProps.highlightedGreen.value,
        'vas-highlighted-red': dataProps.highlightedRed.value,
      };
    });

    const segments = [
      { from: 50, to: 70, color: '#b70042ff', label: 'ЭЛЕМЕНТАРНЫЙ' },
      { from: 70, to: 90, color: '#f1b913ff', label: 'НАЧАЛЬНЫЙ' },
      { from: 90, to: 110, color: '#8be02bff', label: 'СРЕДНИЙ' },
      { from: 110, to: 130, color: '#22a441ff', label: 'ВЫСОКИЙ' },
      { from: 130, to: 150, color: '#993debff', label: 'ВЫСШИЙ' },
    ];

    const dividerPositions = computed(() => {
      const positions = segments.map(s => s.from);
      positions.push(segments[segments.length - 1].to);
      return positions.map(position => ((position - 50) / total) * 100).sort((a, b) => a - b);
    });

    const pointerStyle = computed(() => {
      const clamped = Math.min(Math.max(dataProps.value.value, 50), 150);
      return {
        left: `${((clamped - 50) / total) * 100}%`,
      };
    });

    const arrowColor = computed(() => {
      const clamped = Math.min(Math.max(dataProps.value.value, 50), 150);
      for (let segment of segments) {
        if (clamped >= segment.from && clamped <= segment.to) {
          return segment.color;
        }
      }
      return '#2b2d42';
    });

    const coloredFillStyle = computed(() => {
      const clamped = Math.min(Math.max(dataProps.value.value, 50), 150);// диапазон от 50 до 150
      const gradientStops = [];

      const getPercent = (val) => ((val - 50) / total) * 100;

      const gradientSegments = [
        {
          min: 50,
          stops: [
            [`#b70042`, 60],
            [`#b70042`, 64],
            [`#c4222f`, 66.8],
            [`#d13926`, 67.5],
            [`#dd4e21`, 68.2],
            [`#e8631d`, 69],
            [`#ef6f1f`, 70],
            [`#f88d1f`, 71],
            [`#f19820`, 72],
          ],
        },
        {
          min: 70,
          stops: [
            [`#f19820`, 70],
            [`#f2d604`, 86],
            [`#f2e204`, 86],
          ],
        },
        {
          min: 90,
          stops: [
            [`#f2e204`, 88],
            [`#d4f12e`, 92],
            [`#aff424`, 100],
            [`#9be93d`, 105],
          ],
        },
        {
          min: 110,
          stops: [
            [`#26a147`, 110],
            [`#21a540`, 127],
          ],
        },
        {
          min: 130,
          stops: [
            [`#21a540`, 128],
            [`#2a8b65`, 130],
            [`#5c62b2`, 131],
            [`#7b4ed5`, 133.5],
            [`#993deb`, 135],
          ],
        },
      ];

      for (const segment of gradientSegments) {
        if (clamped >= segment.min) {
          for (const [color, value] of segment.stops) {
            gradientStops.push(`${color} ${getPercent(value)}%`);
            if (value > clamped) break;
          }
        }
      }

      return {
        width: `100%`,
        background: `linear-gradient(to right, ${gradientStops.join(', ')})`,
        position: 'absolute',
        top: 0,
        left: 0,
        height: '100%',
      };
    });

    const greyFillStyle = computed(() => {
      const clamped = Math.min(Math.max(dataProps.value.value, 50), 150);
      const leftPercent = ((clamped - 50) / total) * 100;

      return {
        position: 'absolute',
        left: `${leftPercent}%`,
        width: `${100 - leftPercent}%`,
        height: '100%',
        background: '#сссccc',
        pointerEvents: 'none',
      };
    });


    return {
      pointerStyle,
      coloredFillStyle,
      greyFillStyle,
      dividerPositions,
      arrowColor,
      segments,
      total,
      containerClass
    }
  }
}
</script>

<style scoped>
.vas-container {
  position: relative;
  width: 100%;
  max-width: 950px;
  margin: 90px auto;
  font-family: sans-serif;
}

.vas-dividers {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}

.vas-divider {
  position: absolute;
  top: -20px;
  height: calc(100% + 40px);
  width: 1px;
  background-color: #ccc;
}

.vas-pointer {
  position: absolute;
  top: -60px;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 2;
}

.vas-value {
  font-size: 20px;
  font-weight: bold;
  color: #2b2d42;
}

.vas-arrow {
  margin-top: 5px;
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-top: 15px solid;
}

.vas-bar {
  position: relative;
  display: flex;
  height: 40px;
  overflow: hidden;
  z-index: 1;
}

.vas-colored-fill,
.vas-grey-fill {
  position: absolute;
  top: 0;
  height: 100%;
  z-index: 0;
}

.vas-colored-fill {
  left: 0;
}

.vas-grey-fill {
  background-color: #e0e0e0;
}

.vas-segment {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  color: #fff;
  font-weight: bold;
  font-size: 11px;
}

.segment-label {
  padding: 0 4px;
  text-align: center;
  white-space: nowrap;
  color: #000;
}

.vas-scale {
  position: relative;
  margin-top: 6px;
  height: 16px;
  z-index: 1;
}

.tick {
  position: absolute;
  transform: translateX(-50%);
  font-size: 11px;
  color: #666;
}

.scale-label {
  margin-top: 25px;
  font-size: 14px;
  text-align: center;
  color: #333;
  position: absolute;
  bottom: -35px;
  width: 100%;
}

.vas-highlighted-green {
  border: 2px solid #22a441;

}

.vas-highlighted-red {
  border: 2px solid #b70042;
}

.vas-highlighted {
  border-radius: 8px;
  box-sizing: border-box;
  padding-bottom: 20px;

  .vas-pointer {
    position: relative;
    top: 0;
    margin-bottom: 10px;
  }

  .vas-dividers {
    top: 40px;
    height: 50px;
    z-index: 1;
  }

  .vas-divider {
    top: 0;
    height: calc(100% + 60px);
  }

  .scale-label {
    position: relative;
    top: 0;
  }
}
</style>
